import { CheckCircle } from '@mui/icons-material';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import DisabledByDefaultIcon from '@mui/icons-material/DisabledByDefault';
import hotToast, { Toaster as HotToaster } from 'react-hot-toast';
import { IconButton } from '@mui/material';
import { FaExternalLinkAlt } from 'react-icons/fa';
import { cn } from '../../utilities/cn';
import { getToasterColours } from './toaster-colours';

export const Toaster = HotToaster;

export type RaptorToastType =
  | 'success'
  | 'error'
  | 'warning'
  | 'default'
  | 'loading'
  | 'link';

interface ToastProps extends React.HTMLAttributes<HTMLDivElement> {
  visible: boolean;
  title: string;
  message?: string | React.ReactNode;
  icon?: React.ReactNode;
  closeToast: () => void;
  /** When set, toast is clickable and the right control is a link icon. */
  onAction?: () => void;
}

interface ToastOpts {
  title: string;
  message?: string | React.ReactNode;
  type?: RaptorToastType;
  duration?: number;
  className?: string;
  /** Required for `type: 'link'` — navigates / runs action and dismisses. */
  onClick?: () => void;
}

export function Toast({
  visible,
  icon,
  className,
  title,
  message,
  closeToast,
  onAction,
  ...props
}: ToastProps) {
  const isLink = Boolean(onAction);

  return (
    <div
      {...props}
      className={cn(
        'flex w-fit items-center justify-center gap-2 rounded-lg bg-white p-2 shadow-md',
        isLink && 'cursor-pointer',
        className,
      )}
      style={{
        visibility: visible ? 'visible' : 'hidden',
      }}
      onClick={onAction}
      role={isLink ? 'button' : undefined}
      tabIndex={isLink ? 0 : undefined}
      onKeyDown={
        isLink
          ? (event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              onAction?.();
            }
          }
          : undefined
      }
    >
      {icon}
      <div>
        <p className="ml-2 text-nowrap font-bold">{title}</p>
        <p className="ml-2 text-left text-sm">{message}</p>
      </div>
      {isLink ? (
        <IconButton
          onClick={(event) => {
            event.stopPropagation();
            onAction?.();
          }}
          className="p-2 text-inherit"
          aria-label="Open"
        >
          <FaExternalLinkAlt className="h-4 w-4" aria-hidden />
        </IconButton>
      ) : (
        <IconButton
          onClick={(event) => {
            event.stopPropagation();
            closeToast();
          }}
          className="p-2"
        >
          <span className="sr-only">Close</span>
          <DisabledByDefaultIcon className="text-lg text-black" />
        </IconButton>
      )}
    </div>
  );
}

export function toast(opts: ToastOpts) {
  const {
    title,
    message,
    type = 'default',
    duration = 3000,
    className,
    onClick,
  } = opts;

  const isLink = type === 'link';

  return hotToast.custom(
    (t) => {
      const { border, text } = getToasterColours(type);

      return (
        <Toast
          title={title}
          message={message}
          visible={t.visible}
          className={cn(`border-2 ${border} ${text}`, className)}
          icon={
            type === 'success' || type === 'link' ? (
              <CheckCircle />
            ) : type === 'error' ? (
              <ErrorIcon />
            ) : type === 'warning' ? (
              <WarningIcon />
            ) : type === 'loading' ? (
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-blue-500 border-t-transparent"></div>
            ) : null
          }
          onAction={
            isLink && onClick
              ? () => {
                onClick();
                hotToast.remove(t.id);
              }
              : undefined
          }
          closeToast={() => {
            hotToast.remove(t.id);
          }}
        />
      );
    },
    {
      duration: type === 'loading' ? Infinity : duration,
    },
  );
}
